import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createExpense, getExpenses,
  updateExpense, deleteExpense, getSingleExpense
} from '../../redux/features/expense/expenseSlice';
import { toast } from 'react-toastify';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import ReactToPrint from 'react-to-print';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "bootstrap";
import { EthiopianDateCell } from './EthiopianDateCell';
import { SimpleDualDateInput } from '../DualDateInput';


const initialState = {
  type:          "",
  amount:        "",
  description:   "",
  expenseMethod: "",
  date:          "",
};

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

const formatDate = (d) => d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : '';

export const Expense = () => {
  useRedirectLogOutUser();

  const dispatch     = useDispatch();
  const componentRef = useRef();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);

  const [expenseForm, setExpenseForm] = useState(initialState);
  const [isLoading, setIsLoading]     = useState(false);
  const [printView, setPrintView]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [deleteId, setDeleteId]       = useState(null);

  const { expenses, expense } = useSelector((state) => state.expense);
  const { userType }          = useSelector((state) => state.auth);
  const isAdmin               = userType === "admin";

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getExpenses());
  }, [dispatch]);

  // Populate edit form
  useEffect(() => {
    if (expense && editId) {
      setExpenseForm({
        type:          expense.type          ?? "",
        amount:        expense.amount        ?? "",
        description:   expense.description   ?? "",
        expenseMethod: expense.expenseMethod ?? "",
        date:          expense.date
          ? new Date(expense.date).toISOString().split("T")[0]
          : expense.createdAt
            ? new Date(expense.createdAt).toISOString().split("T")[0]
            : "",
      });
    }
  }, [expense, editId]);

  // ── Modal openers ──────────────────────────────────────────
  const openAddModal = () => {
    setExpenseForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleExpense(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  // ── Input handler ──────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm(prev => ({ ...prev, [name]: value }));
  };

  // ── Add submit — direct, modal stays open ─────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const { type, amount, expenseMethod } = expenseForm;
    if (!type || !amount || !expenseMethod) {
      return toast.error("Purpose, amount and method are required");
    }
    setIsLoading(true);
    await dispatch(createExpense({
      type,
      amount:        Number(expenseForm.amount),
      description:   expenseForm.description   || null,
      expenseMethod: expenseForm.expenseMethod,
      date:          expenseForm.date          || null,
    }));
    setIsLoading(false);
    // ✅ Reset form but keep modal open
    setExpenseForm(initialState);
    toast.success("Expense saved — add another or close.");
  };

  // ── Edit submit ────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateExpense({
      id:   editId,
      data: {
        type:          expenseForm.type,
        amount:        Number(expenseForm.amount),
        description:   expenseForm.description   || null,
        expenseMethod: expenseForm.expenseMethod,
        date:          expenseForm.date          || null,
      }
    }));
    dispatch(getExpenses());
    Modal.getInstance(editModalRef.current).hide();
  };

  // ── Delete ─────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteExpense(deleteId));
    Modal.getInstance(deleteModalRef.current).hide();
  };

  const handlePrint = () => setPrintView(true);
  const handleBeforeGetContent = async () => await setPrintView(true);

  // ── Shared form fields ─────────────────────────────────────
  const renderFormFields = () => (
    <div className="row g-3">

      <div className="col-12">
        <label className="h4">Purpose of Expense</label>
        <input
          className="form-control form-control-lg border-2"
          type="text"
          placeholder="e.g. Delivery charges, Rent..."
          name="type"
          value={expenseForm.type}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-6">
        <label className="h4">Amount</label>
        <input
          className="form-control form-control-lg border-2"
          type="number" min="0"
          placeholder="0.00"
          name="amount"
          value={expenseForm.amount}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-6">
        <label className="h4">Expense Method</label>
        <select
          className="form-select form-select-lg border-2"
          name="expenseMethod"
          value={expenseForm.expenseMethod}
          onChange={handleInputChange}
        >
          <option value="">Select method...</option>
          <option value="Cash">Cash</option>
          <option value="Bank">Bank</option>
        </select>
      </div>

      <div className="col-6">
        <SimpleDualDateInput
          label="Date"
          value={expenseForm.date}
          onChange={(date) => setExpenseForm(prev => ({ ...prev, date }))}
          maxDate={new Date()}
        />
      </div>

      <div className="col-12">
        <label className="h4">Description</label>
        <textarea
          className="form-control border-2"
          name="description"
          value={expenseForm.description}
          onChange={handleInputChange}
          rows={2}
          placeholder="Optional description..."
        />
      </div>

    </div>
  );

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(() => {
    const base = [
      { Header: 'No', accessor: 'no', Cell: ({ row }) => row.index + 1 },
      {
        Header: 'Purpose', accessor: 'type',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Amount', accessor: 'amount',
        Cell: ({ value }) => value != null
          ? <span className="fw-semibold text-danger">{Number(value).toLocaleString()} birr</span>
          : "—"
      },
      {
        Header: 'Method', accessor: 'expenseMethod',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => value
          ? <span className={`badge rounded-pill px-3 py-2 fs-6 ${value === "Cash" ? "bg-success" : "bg-primary"}`}>
              {value}
            </span>
          : "—"
      },
      {
        Header: 'Description', accessor: 'description',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Date', accessor: 'createdAt',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"
      },
    ];

    if (!printView && isAdmin) {
      base.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>
            <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}>
              <MdDeleteForever size={24} />
            </span>
          </>
        )
      });
    }

    return base;
  }, [printView, isAdmin]);

  const data        = useMemo(() => Array.isArray(expenses) ? expenses : [], [expenses]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, data, filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow,
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── ADD MODAL ─────────────────────────────────────────── */}
      <div className="modal fade" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Expense</h3>
                  <small className="text-muted fs-5">
                    Saves immediately — modal stays open to add more.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields()}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal">
                  Close
                </button>
                {isLoading ? (
                  <ButtonLoading
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2"
                    disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                    Save Expense
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────────── */}
      <div className="modal fade" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Expense</h3>
                  <small className="text-muted fs-5">
                    Update expense details and save changes.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields()}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal">
                  Cancel
                </button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                  Update Expense
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────────── */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">

            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Expense</h5>
                  <small className="text-muted">This action cannot be undone.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">
                  Are you sure you want to delete this expense?
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button
                className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                className="btn btn-danger flex-fill rounded-3 fw-medium py-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />
                Delete
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ── HEADER ────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0 fs-2">Expenses</h4>
        <div className="d-flex gap-2">
          <ReactToPrint
            trigger={() => (
              <button className="btn btn-primary rounded-3 fs-2" onClick={handlePrint}>
                Print Table
              </button>
            )}
            content={() => componentRef.current}
            onAfterPrint={() => setPrintView(false)}
            onBeforeGetContent={handleBeforeGetContent}
          />
          {isAdmin && (
            <button className="btn btn-primary rounded-3 fs-2" onClick={openAddModal}>
              <AiOutlinePlus className="me-1 fs-2" /> Add Expense
            </button>
          )}
        </div>
      </div>

      {/* ── GLOBAL FILTER ─────────────────────────────────────── */}
      <div className="px-4 mb-4">
        <div className="d-flex align-items-center px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>
      </div>

      {/* ── TABLE ─────────────────────────────────────────────── */}
      <div className="px-4">
        <div style={{ margin: printView ? "30px" : null }} ref={componentRef}>
          {printView && (
            <>
              <div style={{ padding: "1.5rem" }}><h2>Expenses</h2></div>
              <div style={{ marginBottom: '1rem', textAlign: "right", fontSize: "1.5rem" }}>
                Date: {new Date().toLocaleDateString()}
              </div>
            </>
          )}
          <div className="table-responsive rounded-4 border mt-5" style={{ backgroundColor: "#ffffff" }}>
            <table className="table table-hover table-striped caption-top fs-4"
              {...getTableProps()}
              style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th className="px-4"
                        style={{ backgroundColor: "#faf8f5" }}
                        {...column.getHeaderProps()}>
                        {column.render('Header')}
                        {!printView && column.canFilter && column.render('Filter')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td className="px-4"
                          style={{ backgroundColor: "#ffffff" }}
                          {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan={columns.length}
                      className="text-center text-muted py-4">
                      No expenses found.
                    </td>
                  </tr>
                )}
              </tbody>
              {/* Total row */}
              {data.length > 0 && (
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan={2} className="px-4 py-2">Total</td>
                    <td className="px-4 py-2 text-danger fw-bold">
                      {data.reduce((sum, e) => sum + (Number(e.amount) || 0), 0).toLocaleString()} birr
                    </td>
                    <td colSpan={columns.length - 3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: '15px' }}>
            page{' '}
            <strong>
              <em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}
            </strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: '90px' }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 15, 20, 30, 40, 50, data.length].map((size, i) => (
              <option key={i} value={size}>Show {size}</option>
            ))}
          </select>
          <li>
            <button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>
          </li>
          <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li>
            <button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>
          </li>
        </ul>
      </div>

    </div>
  );
};